import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, Validators } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { CoreModule } from 'app/core/core.module';
import { peerToPeerReport } from 'app/manage-p2p/features/p2p.actions';
import { selectFundraiserReport } from 'app/manage-p2p/features/p2p.selectors';
import { ModalP2pAddBulkMilestonesService } from 'app/manage-p2p/services/modal-p2p-add-bulk-milestones/modal-p2p-add-bulk-milestones.service';
import { PeerToPeerManageService } from 'app/manage-p2p/services/peer-to-peer-manage/peer-to-peer-manage.service';
import { ToastService } from 'app/toast/services/toast.service';
import { MdbFormsModule } from 'mdb-angular-ui-kit/forms';
import { from } from 'rxjs';
import { concatMap, finalize, last } from 'rxjs/operators';

@Component({
  selector: 'app-p2p-add-bulk-milestones-modal',
  standalone: true,
  imports: [
    CommonModule, 
    FormsModule, 
    ReactiveFormsModule, 
    CoreModule, 
    MdbFormsModule
  ],
  templateUrl: './p2p-add-bulk-milestones-modal.component.html',
  styleUrl: './p2p-add-bulk-milestones-modal.component.scss'
})
export class P2pAddBulkMilestonesModalComponent implements OnInit {
  @Input() data;
  
  destroyRef = inject(DestroyRef);

  public campaign: any = null;
  public placeOfCall: string = '';

  public eventCountErrorText: string = 'Invalid Count';
  public maxEventCount: number = 0;

  private fundraiserReport$ = this.store.pipe(
    select(selectFundraiserReport)
  );

  public donorReport: any[] = [];
  public form = new FormGroup({});

  constructor(
    public modalP2pAddBulkMilestonesService: ModalP2pAddBulkMilestonesService,
    private store: Store,
    private peerToPeerManageService: PeerToPeerManageService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.campaign = this.data.campaign;
    this.placeOfCall = this.data.placeOfCall;

    this.maxEventCount = this.campaign.campaignTypeData?.maxEventCount || 0;
    this.eventCountErrorText = `Max Milestones count is ${this.maxEventCount}`

    if (this.placeOfCall === 'campaign-card') {
      this.store.dispatch({
        type: peerToPeerReport.type,
        payload: this.campaign.id
      });
    }

    this.fundraiserReport$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((donorReport: any[]) => {
      this.donorReport = donorReport;
      this.initForm();
    });
  }

  initForm() {
    const formControls = {};
    
    this.donorReport.forEach(donor => {
      if (donor.id) {
        formControls[donor.id] = new FormControl('', [
          Validators.min(0),
          Validators.max(this.maxEventCount)
        ]);

        if (donor.eventCount > 0) {
          formControls[donor.id].setValue(donor.eventCount);
        }
      }
    });

    this.form = new FormGroup(formControls);
  }

  addMilestones() {
    this.form.markAllAsTouched();
    
    if (!this.form.valid) {
      const firstInvalidControl = Object.keys(this.form.controls)
        .find(key => this.form.controls[key].invalid);

      if (firstInvalidControl) {
        setTimeout(() => {
          const element = document.getElementById(firstInvalidControl);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
      }
      return;
    }

    const updates = Object.keys(this.form.controls)
      .map(donorId => {
        const eventCount = parseInt(this.form.get(donorId).value);
        const donor = this.donorReport.find(d => d.id === donorId);
        const prevEventCount = donor?.eventCount || 0;
        
        if (donor && eventCount >= 0 && eventCount !== prevEventCount) {
          return {
            campaignId: this.campaign.id,
            p2pId: donorId,
            eventCount: eventCount
          };
        }
        return null;
      })
      .filter(update => update !== null);

    if (updates.length > 0) {
      from(updates).pipe(
        concatMap(params => this.peerToPeerManageService.saveP2PEventCount(params)),
        last()
      ).subscribe({
        next: () => {
          if (this.placeOfCall === 'campaign-details') {
            this.store.dispatch({
              type: peerToPeerReport.type,
              payload: this.campaign.id
            });
          }
          this.toastService.show('success', 'Saved successfully!', {delay: 2000});
          this.close();
        },
        error: (error) => {
          console.error('Error updating milestones:', error);
          this.close();
        }
      });
    } else {
      this.close();
    }
  }

  getControlName(donorId: string): string {
    return donorId;
  }

  close() {
    this.modalP2pAddBulkMilestonesService.close();
  }
}
